import React, { useEffect, useState } from 'react';
import { X, Minus, Plus, ShoppingCart, Sparkles, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { useCart } from '@/context/CartContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../ui/sheet';
import { BundleBuilderModal } from './BundleBuilderModal';
import { menuAPI } from '../../utils/api';
import { toast } from 'sonner';
import * as meta from '../../analytics/meta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CartSheet = () => {
  const navigate = useNavigate();
  const { cartItems, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, getTotalPrice, addToCart } = useCart();
  const total = getTotalPrice();
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [editingBundle, setEditingBundle] = useState(null); // { bundle, lineKey, quantity, selections }

  // Fetch suggested products when cart opens
  useEffect(() => {
    if (isCartOpen) {
      fetchSuggestedProducts();
    }
  }, [isCartOpen]);

  const fetchSuggestedProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/suggested-products`);
      setSuggestedProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch suggested products:', error);
    }
  };

  const handleAddSuggested = (item) => {
    addToCart({
      item_id: item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
      image_url: item.image_url,
      customizations: {}
    });
  };

  // Filter out items already in cart (compare by underlying item_id)
  const cartItemIds = cartItems.map(item => item.item_id);
  const filteredSuggestions = suggestedProducts.filter(
    s => !cartItemIds.includes(s.menu_item?.id)
  );

  const handleCheckout = () => {
    // Fire Meta Pixel `InitiateCheckout` right before we route so Ads
    // Manager can build a Custom Audience of "started checkout but did
    // not purchase" for retargeting campaigns.
    meta.initiateCheckout(cartItems, total);
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full p-0" data-testid="cart-sheet">
        <SheetHeader className="px-6 pt-6 pb-2 shrink-0">
          <SheetTitle className="text-2xl font-bold">Your Cart</SheetTitle>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <ShoppingCart className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-muted-foreground">Your cart is empty</p>
              <p className="text-sm text-muted-foreground mt-2">Add some delicious items to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => {
                const isBundle = (item.item_type || 'standard') === 'bundle';
                return (
                <div key={item.line_key || item.item_id} className="flex gap-4 bg-secondary/50 rounded-xl p-4" data-testid="cart-item">
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-base">{item.name}</h4>
                          {isBundle && (
                            <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary font-semibold px-2 py-0.5 rounded-full">
                              Meal Deal
                            </span>
                          )}
                        </div>
                        {/* Bundle selections */}
                        {isBundle && Array.isArray(item.bundle_selections) && item.bundle_selections.length > 0 && (
                          <div className="mt-1 space-y-0.5" data-testid="cart-bundle-selections">
                            {(() => {
                              // Group selections by person label to render nicely
                              const grouped = {};
                              item.bundle_selections.forEach((s) => {
                                const key = s.person_label || '__solo__';
                                grouped[key] = grouped[key] || [];
                                grouped[key].push(s);
                              });
                              return Object.entries(grouped).map(([label, picks]) => (
                                <div key={label}>
                                  {label !== '__solo__' && (
                                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mt-1">
                                      {label}
                                    </p>
                                  )}
                                  {picks.map((p, i) => (
                                    <div key={i}>
                                      <p className="text-xs text-foreground/80">• {p.display_name}
                                        {p.unit_surcharge > 0 && (
                                          <span className="text-primary"> +£{Number(p.unit_surcharge).toFixed(2)}</span>
                                        )}
                                      </p>
                                      {p.nested_customizations && Object.entries(p.nested_customizations).map(([mg, choices]) => {
                                        const arr = Array.isArray(choices) ? choices : [choices];
                                        return arr.map((c, j) => (
                                          <p key={mg + j} className="text-[11px] text-muted-foreground pl-3">
                                            – {c.name}{Number(c.price) > 0 ? ` +£${Number(c.price).toFixed(2)}` : ''}
                                          </p>
                                        ));
                                      })}
                                    </div>
                                  ))}
                                </div>
                              ));
                            })()}
                            {Array.isArray(item.fixed_items_snapshot) && item.fixed_items_snapshot.length > 0 && (
                              <div className="mt-1">
                                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                                  Also included
                                </p>
                                {item.fixed_items_snapshot.map((fx, i) => (
                                  <p key={i} className="text-xs text-muted-foreground">
                                    • {fx.quantity}× {fx.name}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {/* Standard customizations */}
                        {!isBundle && item.customizations && Object.keys(item.customizations).length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {Object.entries(item.customizations).map(([optionId, selection]) => {
                              if (Array.isArray(selection) && selection.length > 0) {
                                return (
                                  <p key={optionId} className="text-xs text-muted-foreground">
                                    + {selection.map(s => s.name).join(', ')}
                                  </p>
                                );
                              } else if (selection && selection.name) {
                                return (
                                  <p key={optionId} className="text-xs text-muted-foreground">
                                    • {selection.name}
                                  </p>
                                );
                              }
                              return null;
                            })}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {isBundle && (
                          <Button
                            data-testid="edit-bundle-button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 -mt-1"
                            onClick={async () => {
                              try {
                                // Use the PUBLIC menu endpoint — the admin-only
                                // /api/menu/all returns 401 for real customers
                                // and would silently break editing.
                                const res = await menuAPI.getMenu();
                                const bundle = (res.data || []).find((m) => m.id === item.item_id);
                                if (!bundle) {
                                  toast.error('This meal deal is no longer available. Please remove it and add a new one.');
                                  return;
                                }
                                setEditingBundle({
                                  bundle,
                                  lineKey: item.line_key || item.item_id,
                                  quantity: item.quantity,
                                  selections: item.bundle_selections || [],
                                });
                              } catch (err) {
                                toast.error('Unable to open the meal editor — please try again.');
                              }
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          data-testid="remove-item-button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 -mt-1 -mr-2"
                          onClick={() => removeFromCart(item.line_key || item.item_id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-primary mt-1">£{item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        data-testid="decrease-quantity-button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 rounded-full"
                        onClick={() => updateQuantity(item.line_key || item.item_id, item.quantity - 1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="text-sm font-medium w-8 text-center" data-testid="item-quantity">{item.quantity}</span>
                      <Button
                        data-testid="increase-quantity-button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 rounded-full"
                        onClick={() => updateQuantity(item.line_key || item.item_id, item.quantity + 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );})}

              {/* Suggested Products Section */}
              {filteredSuggestions.length > 0 && (
                <div className="pt-4 border-t" data-testid="suggested-products-cart">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <h4 className="font-semibold text-sm">You might also like</h4>
                  </div>
                  <div className="space-y-2">
                    {filteredSuggestions.slice(0, 3).map((suggestion) => (
                      <div 
                        key={suggestion.suggestion_id} 
                        className="flex items-center justify-between bg-primary/5 rounded-lg p-3"
                        data-testid="suggested-product-item"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{suggestion.menu_item?.name}</p>
                          <p className="text-xs text-primary font-bold">£{suggestion.menu_item?.price?.toFixed(2)}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full h-8 px-3 ml-2"
                          onClick={() => handleAddSuggested(suggestion.menu_item)}
                          data-testid="add-suggested-button"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {cartItems.length > 0 && (
          <SheetFooter className="border-t pt-4 px-6 pb-6 shrink-0">
            <div className="w-full space-y-4">
              <div className="flex justify-between items-center text-lg font-bold">
                <span>Total:</span>
                <span className="text-primary" data-testid="cart-total">£{total.toFixed(2)}</span>
              </div>
              <Button
                data-testid="proceed-checkout-button"
                className="w-full rounded-full h-12 text-base"
                onClick={handleCheckout}
              >
                Proceed to Checkout
              </Button>
            </div>
          </SheetFooter>
        )}
        {editingBundle && (
          <BundleBuilderModal
            bundle={editingBundle.bundle}
            open={!!editingBundle}
            initialSelections={editingBundle.selections}
            initialQuantity={editingBundle.quantity}
            onClose={() => setEditingBundle(null)}
            onAdd={(payload) => {
              // Replace the existing bundle line with the newly edited one.
              removeFromCart(editingBundle.lineKey);
              addToCart(payload);
              setEditingBundle(null);
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  );
};
